import React, { useState } from 'react';
import { Skeleton } from '@components/atoms/Skeleton';
import ErrorMessage from '@components/molecules/ErrorMessage';
import { EmptyData } from '@components/molecules/EmptyData';
import { EvidenceSummaryBar } from '@components/organisms/EvidenceGrid/EvidenceSummaryBar';
import { EvidenceSlotCard } from '@components/organisms/EvidenceGrid/EvidenceSlotCard';
import { EvidenceCaptureLane } from '@components/organisms/EvidenceGrid/EvidenceCaptureLane';
import { EvidenceLightbox } from '@components/organisms/EvidenceGrid/EvidenceLightbox';
import { useEvidenceSlotsByProject } from '@hooks/api';
import { EvidenceCaptureRef } from '@services/evidenceSlot';
import { useTranslation } from '@hooks/useTranslation';

interface ProjectEvidenceTabProps {
  projectId: string;
}

/**
 * Sprint 11, story-042 (U77). Per-task evidence breakdown for a project — reads story-041's
 * `getEvidenceSlotStatusByProject` (one entry per task) so a PM can scan every task's evidence in
 * one project-level screen, per docs/architecture.md's Sprint 11 section ("mobile always captures
 * evidence against a task"). No client-side fulfillment logic — renders the DTO's `status` field
 * directly, like `EvidenceGrid`.
 */
export const ProjectEvidenceTab: React.FC<ProjectEvidenceTabProps> = ({ projectId }) => {
  const tProjectComponents = useTranslation('projectComponents');
  const t = tProjectComponents.evidenceTab;
  const { data: taskSlots, isLoading, isError } = useEvidenceSlotsByProject(projectId);
  const [lightboxCapture, setLightboxCapture] = useState<EvidenceCaptureRef | null>(null);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 mt-4">
        {[0, 1].map((i) => (
          <Skeleton key={i} height={220} rounded="lg" />
        ))}
      </div>
    );
  }

  if (isError) {
    return <ErrorMessage message={t.loadError} />;
  }

  if (!taskSlots || taskSlots.length === 0) {
    return <EmptyData description={t.noTasksYet} />;
  }

  return (
    <div className="flex flex-col gap-6 mt-4">
      {taskSlots.map((task) => {
        const requiredSlots = task.slots.filter((s) => s.required);
        const requiredFilled = requiredSlots.filter((s) => s.status === 'FILLED').length;
        const nonRepeatable = task.slots.filter((s) => !s.repeatable);
        const repeatable = task.slots.filter((s) => s.repeatable);

        const openLightbox = (documentId: string) => {
          const found = task.slots.flatMap((s) => s.captures).find((c) => c.documentId === documentId);
          if (found) setLightboxCapture(found);
        };

        return (
          <div key={task.taskId} className="border-b border-frozenGrayShades2 dark:border-frozenGrayShades8 pb-4">
            <h4 className="text-sm font-semibold mb-2">{task.taskName}</h4>
            <EvidenceSummaryBar requiredTotal={requiredSlots.length} requiredFilled={requiredFilled} />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-2">
              {nonRepeatable.map((slot) => (
                <EvidenceSlotCard key={slot.slotId} slot={slot} onOpenLightbox={openLightbox} />
              ))}
            </div>
            {repeatable.map((slot) => (
              <div key={slot.slotId} className="mt-3">
                <h5 className="text-xs font-medium mb-1">
                  {slot.label} ({slot.captures.length})
                </h5>
                <EvidenceCaptureLane captures={slot.captures} onOpenLightbox={openLightbox} />
              </div>
            ))}
          </div>
        );
      })}
      <EvidenceLightbox capture={lightboxCapture} onClose={() => setLightboxCapture(null)} />
    </div>
  );
};

export default ProjectEvidenceTab;
